// RebrandingPanel.jsx - Corporate identity reset

import React, { useState } from 'react';
import { REBRAND_CONFIG, canRebrand, generateBrandSuggestions } from '../../systems/RebrandingSystem.js';
import { debug } from '../../lib/debug.js';

export function RebrandingPanel({ rebrandingState, relationsState, cash, gameDay, airline, onRebrand, onClose }) {
  const [confirming, setConfirming] = useState(false);
  const [suggestions] = useState(() => generateBrandSuggestions());

  const rebrandCheck = canRebrand(rebrandingState || {}, cash, gameDay);

  debug('[RebrandingPanel] Props:', { cash, gameDay, canRebrand: rebrandCheck.canRebrand, reason: rebrandCheck.reason });

  // Count negative relations
  const negativeAirports = Object.values(relationsState?.airports || {}).filter(
    a => (a.loyalty?.PLAYER || 0) < 0
  ).length;

  const negativeCompetitors = Object.values(relationsState?.competitors || {}).filter(
    c => (c.playerRelation || 0) < 0
  ).length;

  const handleRebrand = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    debug('[RebrandingPanel] Executing rebrand');
    onRebrand();
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        <div style={styles.header}>
          <div style={styles.headerTitle}>REBRANDING</div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* Current State */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>CURRENT STANDING</div>
          <div style={styles.currentBrand}>
            <div style={styles.brandName}>{airline?.name || 'Your Airline'}</div>
            <div style={styles.brandCallsign}>{airline?.callsign || 'XXX'}</div>
          </div>

          <div style={styles.relationsSummary}>
            <div style={styles.statRow}>
              <span>Negative Airport Relations:</span>
              <span style={negativeAirports > 0 ? styles.negative : styles.positive}>
                {negativeAirports}
              </span>
            </div>
            <div style={styles.statRow}>
              <span>Hostile Competitors:</span>
              <span style={negativeCompetitors > 0 ? styles.negative : styles.positive}>
                {negativeCompetitors}
              </span>
            </div>
          </div>
        </div>

        {/* Rebrand Effects */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>REBRANDING EFFECTS</div>
          <div style={styles.effectsList}>
            <div style={styles.effectItem}>
              <span style={styles.effectIcon}>+</span>
              <span>Reset all negative airport relations to neutral (0)</span>
            </div>
            <div style={styles.effectItem}>
              <span style={styles.effectIcon}>+</span>
              <span>Reduce competitor hostility by 50%</span>
            </div>
            <div style={styles.effectItem}>
              <span style={styles.effectIcon}>+</span>
              <span>Keep all positive relationships</span>
            </div>
            <div style={{...styles.effectItem, ...styles.warningItem}}>
              <span style={styles.warningIcon}>!</span>
              <span>Must design new livery after rebrand</span>
            </div>
            <div style={{...styles.effectItem, ...styles.warningItem}}>
              <span style={styles.warningIcon}>!</span>
              <span>All active ad campaigns will be cancelled</span>
            </div>
          </div>
        </div>

        {/* Cost & Cooldown */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>REQUIREMENTS</div>
          <div style={styles.requirements}>
            <div style={styles.reqRow}>
              <span>Cost:</span>
              <span style={cash >= REBRAND_CONFIG.cost ? styles.positive : styles.negative}>
                ${formatMoney(REBRAND_CONFIG.cost)}
              </span>
            </div>
            <div style={styles.reqRow}>
              <span>Cooldown:</span>
              <span>{REBRAND_CONFIG.cooldownDays} days between rebrands</span>
            </div>
            {rebrandingState?.lastRebrandAt && (
              <div style={styles.reqRow}>
                <span>Last Rebrand:</span>
                <span>Day {rebrandingState.lastRebrandAt}</span>
              </div>
            )}
            {rebrandingState?.timesRebranded > 0 && (
              <div style={styles.reqRow}>
                <span>Times Rebranded:</span>
                <span>{rebrandingState.timesRebranded}</span>
              </div>
            )}
          </div>
        </div>

        {/* Name Suggestions */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>SUGGESTED NEW NAMES</div>
          <div style={styles.suggestions}>
            {suggestions.map((s, i) => (
              <div key={i} style={styles.suggestion}>
                <span style={styles.suggestionName}>{s.name}</span>
                <span style={styles.suggestionCallsign}>{s.callsign}</span>
              </div>
            ))}
          </div>
          <div style={styles.hint}>You'll choose your new identity in the Livery Editor after rebranding</div>
        </div>

        {/* Action Button */}
        <div style={styles.actions}>
          {!rebrandCheck.canRebrand ? (
            <div style={styles.lockedReason}>{rebrandCheck.reason}</div>
          ) : confirming ? (
            <div style={styles.confirmSection}>
              <div style={styles.confirmWarning}>
                Are you sure? This will cost ${formatMoney(REBRAND_CONFIG.cost)} and cancel all ad campaigns.
              </div>
              <div style={styles.confirmButtons}>
                <button style={styles.cancelBtn} onClick={() => setConfirming(false)}>Cancel</button>
                <button style={styles.confirmBtn} onClick={handleRebrand}>Confirm Rebrand</button>
              </div>
            </div>
          ) : (
            <button style={styles.rebrandBtn} onClick={handleRebrand}>
              Rebrand Airline - ${formatMoney(REBRAND_CONFIG.cost)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '550px', maxHeight: '90vh', overflow: 'auto',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  section: { padding: '20px', borderBottom: '1px solid #1a1a1a' },
  sectionTitle: {
    fontSize: '10px', letterSpacing: '2px', color: '#666', marginBottom: '12px',
  },
  currentBrand: {
    backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '16px',
    marginBottom: '16px', textAlign: 'center',
  },
  brandName: { fontSize: '18px', fontWeight: '600', color: '#fff' },
  brandCallsign: { fontSize: '12px', color: '#666', marginTop: '4px' },
  relationsSummary: { display: 'flex', flexDirection: 'column', gap: '8px' },
  statRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '12px', color: '#888',
  },
  positive: { color: '#00ff88' },
  negative: { color: '#ff4444' },
  effectsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  effectItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '12px', color: '#888',
  },
  effectIcon: { color: '#00ff88', fontSize: '14px' },
  warningItem: { color: '#ff8800' },
  warningIcon: { color: '#ff8800', fontSize: '14px' },
  requirements: { display: 'flex', flexDirection: 'column', gap: '8px' },
  reqRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '12px', color: '#888',
  },
  suggestions: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px',
  },
  suggestion: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '4px', padding: '10px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  suggestionName: { fontSize: '12px', color: '#fff' },
  suggestionCallsign: { fontSize: '10px', color: '#666' },
  hint: { fontSize: '10px', color: '#444', textAlign: 'center' },
  actions: { padding: '20px' },
  lockedReason: {
    textAlign: 'center', padding: '16px',
    fontSize: '12px', color: '#ff4444', fontStyle: 'italic',
  },
  rebrandBtn: {
    width: '100%', padding: '14px',
    background: 'linear-gradient(135deg, #ff4444, #cc2222)',
    border: 'none', borderRadius: '4px',
    fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  confirmSection: { textAlign: 'center' },
  confirmWarning: {
    fontSize: '12px', color: '#ff8800', marginBottom: '16px',
  },
  confirmButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  cancelBtn: {
    padding: '10px 24px',
    background: 'none', border: '1px solid #333', borderRadius: '4px',
    fontSize: '12px', color: '#888', cursor: 'pointer', fontFamily: 'inherit',
  },
  confirmBtn: {
    padding: '10px 24px',
    background: '#ff4444', border: 'none', borderRadius: '4px',
    fontSize: '12px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default RebrandingPanel;
